/**
 * useJobAssignments Hook
 *
 * React Query hook for managing job assignment operations
 *
 * Features:
 * - Fetch assignments with automatic polling
 * - Assign/reassign/unassign jobs
 * - Update priority and time
 * - Optimistic updates with rollback on error
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dispatchApi } from '../api/dispatchApi';
import type { AssignJobRequest, ReassignJobRequest, UnassignJobRequest } from '../types/dispatch.types';
import { toast } from 'sonner';

export function useJobAssignments(driverId?: string) {
  const queryClient = useQueryClient();

  // Fetch assignments
  const {
    data: assignments = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dispatch', 'assignments', driverId],
    queryFn: () => dispatchApi.getAssignments(driverId),
    staleTime: 30000,
    refetchInterval: 60000,
    retry: 2,
  });

  // Assign job
  const assignMutation = useMutation({
    mutationFn: (request: AssignJobRequest) => dispatchApi.assignJob(request),
    onMutate: async (request) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['dispatch', 'assignments'] });

      // Save previous state for rollback
      const previous = queryClient.getQueryData(['dispatch', 'assignments', request.driverId]);
      return { previous, driverId: request.driverId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', 'assignments'] });
      toast.success('Job assigned successfully');
    },
    onError: (error: Error, _variables, context) => {
      // Rollback optimistic update
      if (context?.previous) {
        queryClient.setQueryData(['dispatch', 'assignments', context.driverId], context.previous);
      }
      toast.error(error.message || 'Failed to assign job');
    },
  });

  // Reassign job
  const reassignMutation = useMutation({
    mutationFn: ({ assignmentId, request }: { assignmentId: string; request: ReassignJobRequest }) =>
      dispatchApi.reassignJob(assignmentId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', 'assignments'] });
      toast.success('Job reassigned successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reassign job');
    },
  });

  // Unassign job
  const unassignMutation = useMutation({
    mutationFn: ({ jobId, request }: { jobId: string; request?: UnassignJobRequest }) =>
      dispatchApi.unassignJob(jobId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', 'assignments'] });
      toast.success('Job unassigned successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to unassign job');
    },
  });

  // Update priority
  const updatePriorityMutation = useMutation({
    mutationFn: ({ assignmentId, priority }: { assignmentId: string; priority: number }) =>
      dispatchApi.updatePriority(assignmentId, priority),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', 'assignments'] });
      toast.success('Priority updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update priority');
    },
  });

  // Update time
  const updateTimeMutation = useMutation({
    mutationFn: ({ assignmentId, estimatedStartTime }: { assignmentId: string; estimatedStartTime: string }) =>
      dispatchApi.updateTime(assignmentId, estimatedStartTime),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', 'assignments'] });
      toast.success('Start time updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update time');
    },
  });

  return {
    assignments,
    isLoading,
    error,
    assignJob: assignMutation.mutate,
    reassignJob: reassignMutation.mutate,
    unassignJob: unassignMutation.mutate,
    updatePriority: updatePriorityMutation.mutate,
    updateTime: updateTimeMutation.mutate,
    isAssigning: assignMutation.isPending,
    isReassigning: reassignMutation.isPending,
    isUnassigning: unassignMutation.isPending,
    isUpdatingPriority: updatePriorityMutation.isPending,
    isUpdatingTime: updateTimeMutation.isPending,
  };
}
